import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './PaymentPage.css'; // Import the CSS file

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'stripe-buy-button': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement> & {
        'buy-button-id'?: string;
        'publishable-key'?: string;
      }, HTMLElement>;
    }
  }
}

interface Plan {
  id: string;
  name: string;
  price: string;
  features: string[];
}

const plans: Plan[] = [
  { id: 'free', name: 'Free Plan', price: '$0/month', features: ['10,000 tokens/month', 'Basic API access', 'Community support', '1 project'] },
  { id: 'starter', name: 'Starter Plan', price: '$19.99/month', features: ['50,000 tokens/month', 'Full API access', 'Email support', '5 projects'] },
  { id: 'pro', name: 'Pro Plan', price: '$80/month', features: ['200,000 tokens/month', 'Full API access & higher rate limits', 'Priority email & chat support', 'Unlimited projects', 'Advanced analytics'] },
];

const PaymentPage: React.FC = () => {
  useEffect(() => {
    const scriptId = 'stripe-buy-button-script';
    if (document.getElementById(scriptId)) {
      return; // Script already loaded
    }
    const script = document.createElement('script');
    script.id = scriptId;
    script.src = "https://js.stripe.com/v3/buy-button.js";
    script.async = true;
    document.head.appendChild(script);

    // Optional: Cleanup if needed, though for Stripe.js it's usually fine to leave it.
    // return () => {
    //   const existingScript = document.getElementById(scriptId);
    //   if (existingScript) {
    //     document.head.removeChild(existingScript);
    //   }
    // };
  }, []); // Empty dependency array ensures this runs only once on mount
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [cardholderName, setCardholderName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvc, setCvc] = useState('');

  const handlePaymentSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedPlan) {
      alert('Please select a plan first.');
      return;
    }
    // In a real app, you'd send this data to a payment processor
    console.log('Payment Submitted:', {
      plan: selectedPlan.name,
      cardholderName,
      cardNumber: cardNumber.replace(/\d(?=\d{4})/g, "*"), // Mask card number for logging
      expiryDate,
      cvc: '***',
    });
    alert(`Thank you for choosing the ${selectedPlan.name}! Your payment is being processed (demo).`);
    // Reset form or navigate to a success page
    setSelectedPlan(null);
    setCardholderName('');
    setCardNumber('');
    setExpiryDate('');
    setCvc('');
  };

  return (
    <div className="payment-page-container">
      <header className="payment-header">
        <button type="button" className="back-arrow-button" onClick={() => navigate('/')} aria-label="Go back to homepage">
          <i className="fas fa-arrow-left"></i>
        </button>
        <div className="logo">
          <i className="fas fa-code"></i> Ocody
        </div>
        <h1>Choose Your Plan &amp; Complete Payment</h1>
      </header>

      <div className="plan-selection">
        {plans.map((plan) => (
          <div
            key={plan.id}
            className={`plan-card ${selectedPlan?.id === plan.id ? 'selected' : ''}`}
            onClick={() => setSelectedPlan(plan)}
            role="button"
            tabIndex={0}
            onKeyPress={(e) => e.key === 'Enter' && setSelectedPlan(plan)}
          >
            <h2>{plan.name}</h2>
            <p className="price">{plan.price}</p>
            <ul>
              {plan.features.map((feature, index) => (
                <li key={index}>{feature}</li>
              ))}
            </ul>
            {plan.id === 'starter' && (
              <stripe-buy-button
                buy-button-id="buy_btn_1RW4Y4EiFT1h6Dc1CIHVT1Up"
                publishable-key="pk_live_51LUdMzEiFT1h6Dc1cOJQig8fb54riNLBHsx5jc1J3eNjdZrNWCxiwbIxaj8VAeqdmvkqWC6PnLumqJAt2g7IvBqy00maGpjIDV"
              >
              </stripe-buy-button>
            )}
          </div>
        ))}
      </div>

      {selectedPlan && selectedPlan.id !== 'free' && (
        <div className="payment-details-section">
          <h2>Payment Details for {selectedPlan.name}</h2>
          <form onSubmit={handlePaymentSubmit} className="payment-form">
            <div className="form-group">
              <label htmlFor="cardholderName">Cardholder Name</label>
              <input
                type="text"
                id="cardholderName"
                value={cardholderName}
                onChange={(e) => setCardholderName(e.target.value)}
                required
                autoComplete="cc-name"
              />
            </div>
            <div className="form-group">
              <label htmlFor="cardNumber">Card Number</label>
              <input
                type="text"
                id="cardNumber"
                value={cardNumber}
                placeholder="•••• •••• •••• ••••"
                onChange={(e) => setCardNumber(e.target.value)}
                required
                pattern="[\d ]{16,22}"
                title="Enter a valid card number"
                autoComplete="cc-number"
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="expiryDate">Expiry Date (MM/YY)</label>
                <input
                  type="text"
                  id="expiryDate"
                  value={expiryDate}
                  placeholder="MM/YY"
                  onChange={(e) => setExpiryDate(e.target.value)}
                  required
                  pattern="^(0[1-9]|1[0-2])\/([2-9][0-9])$"
                  title="Enter date as MM/YY"
                  autoComplete="cc-exp"
                />
              </div>
              <div className="form-group">
                <label htmlFor="cvc">CVC</label>
                <input
                  type="text"
                  id="cvc"
                  value={cvc}
                  placeholder="•••"
                  onChange={(e) => setCvc(e.target.value)}
                  required
                  pattern="\d{3,4}"
                  title="Enter 3 or 4 digit CVC"
                  autoComplete="cc-csc"
                />
              </div>
            </div>
            <button type="submit" className="submit-payment-button">
              Confirm Payment for {selectedPlan.price}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default PaymentPage;
